/**
 * @name useTotemServices
 * @description Hook específico para o totem que fornece acesso aos serviços necessários
 * @author Keven Adan Bezerra | kevenadanb
 * @package src/services/supabase/entities/useTotemServices.ts
 */

import { useOperadorContext } from "../../../contexts/OperadorContext";
import { useTotemContext } from "../../../contexts/TotemContext";
import {
  IItemVenda,
  vendaFunctionsService,
} from "../functions/VendaFunctionsService";
import { pessoasService } from "./PessoasService";
import { produtosGruposService } from "./ProdutosGruposService";
import { produtosService } from "./ProdutosService";
import {
  RestricaoResult,
  restricoesAlimentaresService,
} from "./RestricoesAlimentaresService";
import { saldosAlunosService } from "./SaldosAlunosService";
import { saldosFuncionariosService } from "./SaldosFuncionariosService";
import { saldosResponsaveisService } from "./SaldosResponsaveisService";
import { usuariosRfidService } from "./UsuariosRfidService";

/**
 * Hook específico do totem que fornece acesso aos serviços com contexto de organização automático
 * Aguarda operador logar para definir organização
 */
export const useTotemServices = () => {
  const { organizationId, isReady } = useTotemContext();
  const { operadorAtual } = useOperadorContext();

  // Se não há organização definida ainda, retorna serviços desabilitados
  if (!organizationId || !isReady) {
    console.log("⚠️ TotemServices: Aguardando definição da organização...");
    return createDisabledServices();
  }

  const operadorId = operadorAtual?.id || 1; // Fallback para testes

  return {
    // Produtos
    produtos: {
      getAll: (
        page: number,
        pageSize: number,
        sortModel?: any,
        filterModel?: any,
      ) =>
        produtosService.getAll(
          organizationId,
          page,
          pageSize,
          sortModel,
          filterModel,
        ),
      getAllActive: () => produtosService.getAllActive(organizationId),
      getAllProdutos: () => produtosService.getAllProdutos(organizationId),
      getAllWithCategories: () =>
        produtosService.getAllWithCategories(organizationId),
      getById: (id: number) => produtosService.getById(id, organizationId),
      getByGrupo: (grupoId: number) =>
        produtosService.getByGrupo(organizationId, grupoId),
      getBySubGrupo: (subGrupoId: number) =>
        produtosService.getBySubGrupo(organizationId, subGrupoId),
      getEmEstoque: () => produtosService.getEmEstoque(organizationId),
      getDisponiveis: () => produtosService.getDisponiveis(organizationId),
    },

    // Grupos de Produtos
    grupos: {
      getAll: (
        page: number,
        pageSize: number,
        sortModel?: any,
        filterModel?: any,
      ) =>
        produtosGruposService.getAll(
          organizationId,
          page,
          pageSize,
          sortModel,
          filterModel,
        ),
      getAllActive: () => produtosGruposService.getAllActive(organizationId),
      getAllGrupos: () => produtosGruposService.getAllGrupos(organizationId),
      getAllWithProductCount: () =>
        produtosGruposService.getAllWithProductCount(organizationId),
      getById: (id: number) =>
        produtosGruposService.getById(id, organizationId),
    },

    // Pessoas/Alunos
    pessoas: {
      getByMatricula: (matricula: string) =>
        pessoasService.getByMatricula(organizationId, matricula),
      getByEmail: (email: string) =>
        pessoasService.getByEmail(organizationId, email),
      getById: (id: number) => pessoasService.getById(id, organizationId),
    },

    // RFID
    usuariosRfid: {
      autenticarRfid: (tagRfid: string) =>
        usuariosRfidService.autenticarRfid(organizationId, tagRfid),
      getByPessoa: (pessoaId: number) =>
        usuariosRfidService.getByPessoa(organizationId, pessoaId),
      getByRfid: (codigo: string) =>
        usuariosRfidService.getByCodigo(organizationId, codigo),
    },

    // Saldos dos Alunos
    saldosAlunos: {
      getSaldoByAluno: (alunoId: number) =>
        saldosAlunosService.getSaldoByAluno(organizationId, alunoId),
      verificarLimiteCompra: (alunoId: number, valorCompra: number) =>
        saldosAlunosService.verificarLimiteCompra(
          organizationId,
          alunoId,
          valorCompra,
        ),
      debitarSaldo: (
        alunoId: number,
        valor: number,
        vendaId: number,
        descricao: string,
      ) =>
        saldosAlunosService.debitarSaldo(
          organizationId,
          alunoId,
          valor,
          vendaId,
          descricao,
        ),
    },

    // Saldos dos Funcionários
    saldosFuncionarios: {
      getSaldoByFuncionario: (funcionarioId: number) =>
        saldosFuncionariosService.getSaldoByFuncionario(
          organizationId,
          funcionarioId,
        ),
      verificarLimiteCompra: (funcionarioId: number, valorCompra: number) =>
        saldosFuncionariosService.verificarLimiteCompra(
          organizationId,
          funcionarioId,
          valorCompra,
        ),
      debitarSaldo: (
        funcionarioId: number,
        valor: number,
        vendaId: number,
        descricao: string,
      ) =>
        saldosFuncionariosService.debitarSaldo(
          organizationId,
          funcionarioId,
          valor,
          vendaId,
          descricao,
        ),
    },

    // Saldos dos Responsáveis
    saldosResponsaveis: {
      getSaldoByResponsavel: (responsavelId: number) =>
        saldosResponsaveisService.getSaldoByResponsavel(
          organizationId,
          responsavelId,
        ),
      debitarSaldo: (
        responsavelId: number,
        valor: number,
        tipoOrigem: string,
        origemId: number,
        descricao: string,
      ) =>
        saldosResponsaveisService.debitarSaldo(
          organizationId,
          responsavelId,
          valor,
          tipoOrigem,
          origemId,
          descricao,
        ),
    },

    // ✨ Restrições Alimentares (NOVO - Implementação Crítica)
    restricoes: {
      /**
       * Verifica se produtos podem ser comprados por um aluno
       */
      verificarProdutos: (
        alunoId: number,
        produtoIds: number[],
      ): Promise<RestricaoResult> =>
        restricoesAlimentaresService.verificarRestricoesProdutos(
          organizationId,
          alunoId,
          produtoIds,
        ),

      /**
       * Obtém todas as restrições de um aluno
       */
      obterPorAluno: (alunoId: number) =>
        restricoesAlimentaresService.getByAluno(organizationId, alunoId),

      /**
       * Verifica se um produto específico é restringido para um aluno
       */
      verificarProdutoEspecifico: async (
        alunoId: number,
        produtoId: number,
      ): Promise<boolean> => {
        const resultado =
          await restricoesAlimentaresService.verificarRestricoesProdutos(
            organizationId,
            alunoId,
            [produtoId],
          );
        return resultado.permitido;
      },
    },

    // ✨ Funções de Venda (Implementação Database-Driven Completa)
    vendaFunctions: {
      /**
       * Processa uma venda completa com validações
       */
      processar: (
        idPessoa: number,
        itens: IItemVenda[],
        desconto?: number,
        observacoes?: string,
      ) =>
        vendaFunctionsService.processarVenda(
          idPessoa,
          operadorId,
          organizationId,
          itens,
          desconto,
          observacoes,
        ),

      /**
       * Processa venda com verificação automática de restrições
       */
      processarComRestricoes: async (
        idPessoa: number,
        itens: IItemVenda[],
        desconto?: number,
        observacoes?: string,
      ) => {
        try {
          // 1. Verificar restrições alimentares antes de processar
          const produtoIds = itens.map((item) => item.id_produto);
          const verificacaoRestricoes =
            await restricoesAlimentaresService.verificarRestricoesProdutos(
              organizationId,
              idPessoa,
              produtoIds,
            );

          if (!verificacaoRestricoes.permitido) {
            return {
              success: false,
              message: `❌ Venda bloqueada: ${verificacaoRestricoes.motivo}`,
              restricoes: verificacaoRestricoes.restricoes,
            };
          }

          // 2. Processar venda se não há restrições
          console.log("✅ Produtos liberados, processando venda...");
          return await vendaFunctionsService.processarVenda(
            idPessoa,
            operadorId,
            organizationId,
            itens,
            desconto,
            observacoes,
          );
        } catch (error) {
          console.error("❌ Erro no processamento da venda:", error);
          return {
            success: false,
            message: `Erro ao processar venda: ${error instanceof Error ? error.message : "Erro desconhecido"}`,
          };
        }
      },
    },

    // Contexto da organização
    context: {
      organizationId,
      operadorId,
      isReady,
    },
  };
};

/**
 * Cria serviços desabilitados para quando o contexto não está pronto
 */
function createDisabledServices() {
  const disabledMessage = "Organização não definida";
  const disabledResponse = { data: null, error: { message: disabledMessage } };
  const disabledResult = { success: false, message: disabledMessage };

  return {
    produtos: {
      getAll: () => Promise.resolve(disabledResponse),
      getAllActive: () => Promise.resolve(disabledResponse),
      getAllProdutos: () => Promise.resolve(disabledResponse),
      getAllWithCategories: () => Promise.resolve(disabledResponse),
      getById: () => Promise.resolve(disabledResponse),
      getByGrupo: () => Promise.resolve(disabledResponse),
      getBySubGrupo: () => Promise.resolve(disabledResponse),
      getEmEstoque: () => Promise.resolve(disabledResponse),
      getDisponiveis: () => Promise.resolve(disabledResponse),
    },
    grupos: {
      getAll: () => Promise.resolve(disabledResponse),
      getAllActive: () => Promise.resolve(disabledResponse),
      getAllGrupos: () => Promise.resolve(disabledResponse),
      getAllWithProductCount: () => Promise.resolve(disabledResponse),
      getById: () => Promise.resolve(disabledResponse),
    },
    pessoas: {
      getByMatricula: () => Promise.resolve(disabledResponse),
      getByEmail: () => Promise.resolve(disabledResponse),
      getById: () => Promise.resolve(disabledResponse),
    },
    usuariosRfid: {
      autenticarRfid: () => Promise.resolve(disabledResult),
      getByPessoa: () => Promise.resolve(disabledResponse),
      getByRfid: () => Promise.resolve(disabledResponse),
    },
    saldosAlunos: {
      getSaldoByAluno: () => Promise.resolve(disabledResponse),
      verificarLimiteCompra: () => Promise.resolve(disabledResult),
      debitarSaldo: () => Promise.resolve(disabledResult),
    },
    saldosFuncionarios: {
      getSaldoByFuncionario: () => Promise.resolve(disabledResponse),
      verificarLimiteCompra: () => Promise.resolve(disabledResult),
      debitarSaldo: () => Promise.resolve(disabledResult),
    },
    saldosResponsaveis: {
      getSaldoByResponsavel: () => Promise.resolve(disabledResponse),
      debitarSaldo: () => Promise.resolve(disabledResult),
    },
    restricoes: {
      verificarProdutos: () =>
        Promise.resolve({
          permitido: false,
          restricoes: [],
          motivo: disabledMessage,
        }),
      obterPorAluno: () => Promise.resolve([]),
      verificarProdutoEspecifico: () => Promise.resolve(false),
    },
    vendaFunctions: {
      processar: () => Promise.resolve(disabledResult),
      processarComRestricoes: () => Promise.resolve(disabledResult),
    },
    context: {
      organizationId: null,
      operadorId: null,
      isReady: false,
    },
  };
}

export default useTotemServices;
